/**
 * Exchange-rate fetcher with 1-hour localStorage cache and static fallback.
 * Source: open.er-api.com (free, no key, supports BDT and 160+ currencies).
 *
 * Returned rates are USD-based: rates[X] = how many X equal 1 USD.
 */
import type { RateMap } from "@/lib/calculators/currency";

const CACHE_KEY = "bc.fxRates.v1";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const ENDPOINT = "https://open.er-api.com/v6/latest/USD";

export interface FxSnapshot {
  rates: RateMap;
  base: "USD";
  fetchedAt: number;
  source: "live" | "cache" | "fallback";
}

/** Conservative static fallback (illustrative — used only if API fails and cache empty). */
export const FALLBACK_RATES: RateMap = {
  USD: 1,
  GBP: 0.79,
  EUR: 0.92,
  BDT: 121.5,
  INR: 84.2,
  AED: 3.6725,
  SAR: 3.75,
  MYR: 4.45,
  SGD: 1.34,
  JPY: 154.8,
  CAD: 1.37,
  AUD: 1.52,
  CNY: 7.25,
  PKR: 278,
  LKR: 295,
  NPR: 134,
  THB: 35.5,
  KRW: 1380,
  CHF: 0.88,
  HKD: 7.8,
};

const FALLBACK_SNAPSHOT: FxSnapshot = {
  rates: FALLBACK_RATES,
  base: "USD",
  fetchedAt: 0,
  source: "fallback",
};

function readCache(): FxSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Omit<FxSnapshot, "source">;
    if (!parsed?.rates || !parsed?.fetchedAt) return null;
    return { ...parsed, source: "cache" };
  } catch {
    return null;
  }
}

function writeCache(snap: FxSnapshot) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ rates: snap.rates, base: snap.base, fetchedAt: snap.fetchedAt }),
    );
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Returns the freshest rates available. Order:
 * 1. Fresh cache (< 1h) unless force=true.
 * 2. Live API.
 * 3. Stale cache.
 * 4. Static fallback.
 */
export async function getRates(force = false): Promise<FxSnapshot> {
  const cached = readCache();
  if (!force && cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached;
  }

  try {
    const res = await fetch(ENDPOINT, { cache: "no-store" });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const json = (await res.json()) as { result?: string; rates?: RateMap; time_last_update_unix?: number };
    if (json.result !== "success" || !json.rates) throw new Error("invalid payload");
    const snap: FxSnapshot = {
      rates: json.rates,
      base: "USD",
      fetchedAt: Date.now(),
      source: "live",
    };
    writeCache(snap);
    return snap;
  } catch {
    if (cached) return cached; // stale cache beats nothing
    return FALLBACK_SNAPSHOT;
  }
}

/**
 * Frankfurter (ECB) historical time-series. ECB does NOT publish BDT, so
 * BDT pairs return null and the UI hides the chart.
 */
export interface HistoryPoint {
  date: string; // ISO yyyy-mm-dd
  rate: number;
}

const ECB_CURRENCIES = new Set([
  "USD", "GBP", "EUR", "JPY", "CAD", "AUD", "CHF", "CNY", "HKD", "SGD",
  "INR", "KRW", "MYR", "THB", "NZD", "NOK", "SEK", "DKK", "PLN", "CZK",
  "HUF", "ZAR", "BRL", "MXN", "TRY", "ILS", "PHP", "IDR", "RON", "BGN",
  "ISK",
]);

export function supportsHistory(from: string, to: string): boolean {
  return ECB_CURRENCIES.has(from) && ECB_CURRENCIES.has(to);
}

export async function getHistory(from: string, to: string, days = 30): Promise<HistoryPoint[] | null> {
  if (!supportsHistory(from, to)) return null;
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const url = `https://api.frankfurter.app/${fmt(start)}..${fmt(end)}?from=${from}&to=${to}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = (await res.json()) as { rates?: Record<string, Record<string, number>> };
    if (!json.rates) return null;
    return Object.entries(json.rates)
      .map(([date, obj]) => ({ date, rate: obj[to] }))
      .filter((p) => Number.isFinite(p.rate))
      .sort((a, b) => a.date.localeCompare(b.date));
  } catch {
    return null;
  }
}
